import { Injectable } from "@angular/core";
import { Actions, Effect, ofType } from '@ngrx/effects';
import { of } from "rxjs";
import { catchError, combineAll, map, switchMap, tap } from "rxjs/operators";
import { AuthService } from "src/app/auth/auth.service";
import { LoadingPage, StartKeepalive } from "src/app/auth/store";
import { UtilityService } from "src/app/shared/services/utility.service";
import { extensionType } from "../interfaces/search-enum";
import { SearchTokenReq, tokenQuery } from "../interfaces/search-token-req.model";
import { SearchTokenResp } from "../interfaces/search-token-resp.model";
import { SemanticGroupTextualForms } from "../interfaces/semantic-group-textual-forms.model";
import { RicercaService } from "../ricerca.service";
import * as ricercaActions from './ricerca.actions';

@Injectable()
export class RicercaEffects {
    constructor(
        private actions$: Actions,
        private utility: UtilityService,
        private ricercaService: RicercaService,
        private authService: AuthService
    ) { }

    @Effect()
    startGetResourceTree = this.actions$.pipe(
        ofType(ricercaActions.START_GET_RESOURCE_TREE),
        switchMap((action: ricercaActions.StartGetResourceTree) =>
            this.ricercaService.getResourceTree().pipe(
                map(resData => new ricercaActions.GetResourceTreeSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetResourceTreeFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect({ dispatch: false })
    getResourceTreeSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_RESOURCE_TREE_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );

    @Effect()
    startGetPosList = this.actions$.pipe(
        ofType(ricercaActions.START_GET_POS_LIST),
        switchMap((action: ricercaActions.StartGetPosList) =>
            this.ricercaService.getLexiconPos().pipe(
                map(resData => {
                    let cleanedPos: ricercaActions.POS[] = [];
                    resData.forEach(p => cleanedPos.push(<ricercaActions.POS>{ label: p.label }));
                    return new ricercaActions.GetPosListSuccess(cleanedPos);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetPosListFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect()
    getPosListSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_POS_LIST_SUCCESS),
        switchMap(() => {
            this.authService.refreshLogoutTimer();
            return [new StartKeepalive()];
        })
    );

    @Effect()
    startGetLexicalSenses = this.actions$.pipe(
        ofType(ricercaActions.START_GET_LEXICAL_SENSES),
        switchMap((action: ricercaActions.StartGetLexicalSenses) =>
            this.ricercaService.getLexicalSensesList(action.payload).pipe(
                map(resData => new ricercaActions.GetLexicalSensesSuccess(this.utility.mapEntryArray(resData.list))),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetLexicalSensesFailure());
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect()
    getLexicalSensesSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_LEXICAL_SENSES_SUCCESS),
        switchMap(() => {
            this.authService.refreshLogoutTimer();
            return [new StartKeepalive()];
        })
    );

    @Effect()
    startGetFormsList = this.actions$.pipe(
        ofType(ricercaActions.START_GET_FORMS_LIST),
        switchMap((action: ricercaActions.StartGetFormsList) =>
            of(...action.payload.searchT).pipe(
                // map(entry => this.ricercaService.getLexoFormsList(entry)),
                map(entry => this.ricercaService.getLexoFormsListForQE(entry)),
                combineAll(),
                map(resData => {
                    let tempResults: SemanticGroupTextualForms[] = [];
                    resData.forEach(r => tempResults.push(...r));
                    return tempResults;
                }),
                map(resData => {
                    let index = 0;
                    let contatore = 0;
                    let modResData: SemanticGroupTextualForms[] = [];
                    while (index < resData.length) {
                        resData[index].target = this.utility.mapTextualFormArray(resData[index].target);
                        if (resData[index].semanticRelation === '') {
                            resData[index].lexicalEntryInstanceName = action.payload.searchT[contatore].lexicalEntry;
                            modResData.push(resData[index]);
                            contatore++;
                        } else {
                            let semanticRelation = this.utility.splitSemanticGroupRelations(resData[index]);
                            modResData.push(...semanticRelation);
                        }
                        index++;
                    }
                    let result = this.ricercaService.applyMorphologicalFilters(action.payload.queryFormType, action.payload.queryFormName, action.payload.morphTraits, modResData, action.payload.lexicalSenses);
                    return new ricercaActions.GetFormsListSuccess({response:result, resources: action.payload.resources, toRow: action.payload.toRow});
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetFormsListFailure());
                })
            )
        )
    );

    @Effect()
    startGetDescriptionSense = this.actions$.pipe(
        ofType(ricercaActions.START_GET_DESCRIPTION_SENSE),
        switchMap((action: ricercaActions.StartGetDescriptionSense) =>
            of(...action.payload.semanticG).pipe(
                map(entry => {
                    if (entry?.targetSenseInstanceName && entry.targetSenseInstanceName !== '') {
                        return this.ricercaService.getSenseByUsem(entry.targetSenseInstanceName);
                    }
                    return of(null);
                }),
                combineAll(),
                map(senses => {
                    let modifiedSemantic = [...action.payload.semanticG];
                    senses.forEach(s => {
                        if (s !== null) {
                            let indexList: number[] = [];
                            modifiedSemantic.forEach((m, i) => {
                                if (m.targetSenseInstanceName === s.senseInstanceName) {
                                    indexList.push(i);
                                }
                            });
                            indexList.forEach(i => {
                                if (i !== -1) {
                                    modifiedSemantic[i] = { ...modifiedSemantic[i], targetSenseDescription: s.definition[0].propertyValue };
                                }
                            });
                        }
                    });
                    return new ricercaActions.GetFormsListSuccess({response: modifiedSemantic, resources: action.payload.resources, toRow: action.payload.toRow});
                })
            )
        )
    );

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect()
    getFormsListSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_FORMS_LIST_SUCCESS),
        switchMap((action: ricercaActions.GetFormsListSuccess) => {
            this.authService.refreshLogoutTimer();
            let tokens: tokenQuery[] = [];
            action.payload.response.forEach(s => {
                s.target.forEach(t => {
                    let tPos = t.morphology.filter(temp => temp.trait === 'partOfSpeech')[0].value;
                    let token: tokenQuery = <tokenQuery>{ token: t.label, pos: tPos !== '' ? tPos : null };
                    if (!tokens.includes(token)) {
                        tokens.push(token);
                    }
                })
            });
            if(tokens.length > 0) {
                let search: SearchTokenReq = <SearchTokenReq> {
                    tokens: tokens,
                    resources: action.payload.resources,
                    reload: true,
                    highlight: true,
                    fromRow: 0,
                    toRow: action.payload.toRow
                };
                console.log(search.tokens)
                return [
                    new StartKeepalive(),
                    new LoadingPage(true),
                    new ricercaActions.StartGetOccurrenceFromResource(search)
                ];
            }
            return [new StartKeepalive()];
        })
    );

    @Effect()
    startGetIdFormsList = this.actions$.pipe(
        ofType(ricercaActions.START_GET_ID_FORMS_LIST),
        switchMap((action: ricercaActions.StartGetIdFormsList) =>
            of(...action.payload.mus).pipe(
                map(mus => this.ricercaService.getLexoIdFormsList(mus)),
                combineAll(),
                map(resData => {
                    let test: SemanticGroupTextualForms[] = [];
                    for (let i = 0; i < action.payload.mus.length; i++) {
                        test.push(<SemanticGroupTextualForms>{
                            semanticRelation: extensionType.empty,
                            distance: 1,
                            sense: '',
                            senseInstanceName: action.payload.mus[i],
                            target: this.utility.mapTextualFormArray(resData[i])
                        });
                    }
                    let result = this.ricercaService.applyMorphologicalFilters(action.payload.queryFormType, action.payload.queryFormName, action.payload.morphTraits, test, action.payload.lexicalSenses);
                    return new ricercaActions.GetIdFormsListSuccess({response: result, resources: action.payload.resources, toRow: action.payload.toRow}, action.isFromTemplate);
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetIdFormsListFailure());
                })
            )
        )
    );

    @Effect()
    startGetIdFormsListFromTemplate = this.actions$.pipe(
        ofType(ricercaActions.START_GET_ID_FORMS_LIST_FROM_TEMPLATE, ricercaActions.CANCEL_GET_ID_FORMS_LIST_FROM_TEMPLATE),
        switchMap((action: ricercaActions.StartGetIdFormsListFromTemplate | ricercaActions.CancelGetIdFormsListFromTemplate) => {
            if (action.type === ricercaActions.CANCEL_GET_ID_FORMS_LIST_FROM_TEMPLATE) {
                return of();
            } else {
                if (action.payload.idList.length > 0) {
                    let tempIdList: string[] = [...action.payload.idList];
                    let workingIdList: string[] = tempIdList.splice(0, 600);
                    return this.ricercaService.getQueryExpansionForms(workingIdList).pipe(
                        map(resData => new ricercaActions.GetIdFormsListFromTemplatePartial({
                            accumulatore: [...action.payload.accumulatore, <SemanticGroupTextualForms>{
                                semanticRelation: extensionType.empty,
                                distance: 1,
                                sense: '',
                                senseInstanceName: '',
                                target: this.utility.mapTextualFormArray(resData)
                            }], idList: tempIdList,
                            resources: action.payload.resources,
                            toRow: action.payload.toRow
                        })),
                        catchError(errRes => {
                            console.log(errRes);
                            if (errRes.status === 500) {
                                this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                            } else if (errRes.error) {
                                this.utility.gestioneSnackbarErrore(errRes.error);
                            }
                            if (action.payload.accumulatore.length > 0) {
                                let urlSegmentato: string[] = errRes.url.split('/');
                                this.utility.gestioneSnackbarErrorePulsante('Sono state recuperate soltanto le prime ' + action.payload.accumulatore.length + ' entrate. Errore su: ' + urlSegmentato[urlSegmentato.length - 2]);
                                return of(new ricercaActions.GetIdFormsListSuccess({response: action.payload.accumulatore, resources: action.payload.resources, toRow: action.payload.toRow}, true));
                            }
                            return of([new LoadingPage(false), new ricercaActions.GetIdFormsListFailure()]);
                        })
                    )
                } else {
                    return of(new ricercaActions.GetIdFormsListSuccess({response: action.payload.accumulatore, resources: action.payload.resources, toRow: action.payload.toRow}, true));
                }
            }

        })
    );

    @Effect()
    getIdFormsListFromTemplatePartial = this.actions$.pipe(
        ofType(ricercaActions.GET_ID_FORMS_LIST_FROM_TEMPLATE_PARTIAL),
        switchMap((action: ricercaActions.GetIdFormsListFromTemplatePartial) => {
            if (action.payload.idList.length > 0) {
                this.authService.refreshLogoutTimer();
                return [
                    new ricercaActions.StartGetIdFormsListFromTemplate(action.payload),
                    new StartKeepalive()
                ];
            } else {
                return of(new ricercaActions.GetIdFormsListSuccess({response: action.payload.accumulatore, resources: action.payload.resources, toRow: action.payload.toRow}, true));
            }
        })
    )

    /**Side effect per l'avvenuta modifica della voce di glossario */
    @Effect()
    getIdFormsListSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_ID_FORMS_LIST_SUCCESS),
        switchMap((action: ricercaActions.GetIdFormsListSuccess) => {
            this.authService.refreshLogoutTimer();
            let tokens: tokenQuery[] = [];
            action.payload.response.forEach(s => {
                s.target.forEach(t => {
                    let tPos = t.morphology.filter(temp => temp.trait === 'partOfSpeech')[0].value;
                    let token: tokenQuery = <tokenQuery>{ token: t.label, pos: tPos !== '' ? tPos : null };
                    if (!tokens.includes(token)) {
                        tokens.push(token);
                    }
                })
            });
            if(tokens.length > 0) {
                let search: SearchTokenReq = <SearchTokenReq> {
                    tokens: tokens,
                    resources: action.payload.resources,
                    reload: true,
                    highlight: true,
                    fromRow: 0,
                    toRow: action.payload.toRow
                };
                return [
                    new StartKeepalive(),
                    new LoadingPage(true),
                    new ricercaActions.StartGetOccurrenceFromResource(search)
                ];
            }
            return [
                new StartKeepalive()
            ];
        })
    );

    @Effect()
    startCreateLexicalEntry = this.actions$.pipe(
        ofType(ricercaActions.START_CREATE_LEXICAL_ENTRY),
        switchMap((action: ricercaActions.StartCreateLexicalEntry) =>
            this.ricercaService.createLexoLexicalEntry().pipe(
                map(resData => new ricercaActions.CreateLexicalEntrySuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.CreateLexicalEntryFailure());
                })
            )
        )
    );

    @Effect()
    createLexicalEntrySuccess = this.actions$.pipe(
        ofType(ricercaActions.CREATE_LEXICAL_ENTRY_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        }),
        switchMap(() => {
            return [new ricercaActions.LexEntryModalVisibility(true), new StartKeepalive()];
        })
    )

    @Effect()
    startGetOccurrenceFromResource = this.actions$.pipe(
        ofType(ricercaActions.START_GET_OCCURRENCE_FROM_RESOURCE),
        switchMap((action: ricercaActions.StartGetOccurrenceFromResource) =>
            this.ricercaService.getOccurenceFromResource(action.payload).pipe(
                map(resData => {
                    let tempResp: SearchTokenResp = {
                        ...resData,
                        tokens: action.payload.tokens,
                        resources: action.payload.resources,
                        reload: action.payload.reload,
                        highlight: action.payload.highlight
                    };
                    return new ricercaActions.GetOccurrenceFromResourceSuccess(tempResp)
                }),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetOccurrenceFromResourceFailure());
                })
            )
        )
    );

    @Effect()
    getOccurrenceFromResourceSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_OCCURRENCE_FROM_RESOURCE_SUCCESS),
        switchMap((action: ricercaActions.GetOccurrenceFromResourceSuccess) => {
            this.authService.refreshLogoutTimer();
            return [new StartKeepalive(), new LoadingPage(false)];
        })
    )

    @Effect()
    startGetSensesByConcept = this.actions$.pipe(
        ofType(ricercaActions.START_GET_SENSES_BY_CONCEPT),
        switchMap((action: ricercaActions.StartGetSensesByConcept) =>
            this.ricercaService.getReferencedLinguisticObject(action.payload).pipe(
                map(resData => new ricercaActions.GetSensesByConceptSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of([new LoadingPage(false), new ricercaActions.GetSensesByConceptFailure()]);
                })
            )
        )
    )

    @Effect()
    getSensesByConceptSuccess = this.actions$.pipe(
        ofType(ricercaActions.GET_SENSES_BY_CONCEPT_SUCCESS),
        switchMap((action: ricercaActions.GetSensesByConceptSuccess) => {
            this.authService.refreshLogoutTimer();
            return [new LoadingPage(false), new StartKeepalive()];
        })
    );
    @Effect()
    startGetSensesByConcept2 = this.actions$.pipe(
        ofType(ricercaActions.START_GET_SENSES_BY_CONCEPT_2),
        switchMap((action: ricercaActions.StartGetSensesByConcept2) =>
            this.ricercaService.getReferencedLinguisticObject2(action.payload.templateList,action.payload.traitList).pipe(
                map(resData => new ricercaActions.GetSensesByConceptSuccess2(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of([new LoadingPage(false), new ricercaActions.GetSensesByConceptFailure2()]);
                })
            )
        )
    )

    @Effect()
    getSensesByConceptSuccess2 = this.actions$.pipe(
        ofType(ricercaActions.GET_SENSES_BY_CONCEPT_2_SUCCESS),
        switchMap((action: ricercaActions.GetSensesByConceptSuccess2) => {
            this.authService.refreshLogoutTimer();
            return [new LoadingPage(false), new StartKeepalive()];
        })
    );

    @Effect()
    startGetAnalyzedSentence = this.actions$.pipe(
        ofType(ricercaActions.START_GET_ANALYZED_SENTENCE),
        switchMap((action: ricercaActions.StartGetAnalyzedSentence) => 
            this.ricercaService.getAnalyzedSentence(action.payload).pipe(
                map(resData => new ricercaActions.GetAnalyzedSentenceSuccess(resData)),
                catchError(errRes => {
                    if (errRes.status === 500) {
                        this.utility.gestioneSnackbarErrore(this.utility.getTranslatedString('error500'));
                    } else if (errRes.error) {
                        this.utility.gestioneSnackbarErrore(errRes.error);
                    }
                    return of(new ricercaActions.GetAnalyzedSentenceFailure());
                })
            )
        )
    );

    @Effect({dispatch: false})
    getAnalyzedSentenceFailure = this.actions$.pipe(
        ofType(ricercaActions.GET_ANALYZED_SENTENCE_SUCCESS),
        tap(() => {
            this.authService.refreshLogoutTimer();
        })
    );
}